import { Controller } from "@hotwired/stimulus"

const NEXT_KEYS = new Set(["ArrowRight", "ArrowDown"])
const PREVIOUS_KEYS = new Set(["ArrowLeft", "ArrowUp"])

// Connects to data-controller="filter-tabs" on `.lc-filter-tabs__list`.
//
// Keyboard model (APG tabs, manual activation): arrows and Home/End move focus
// between enabled tabs without selecting them, Enter or Space selects the
// focused tab. Native radio groups select on arrow, which here would fire a
// list navigation per keystroke, so arrow keys are intercepted.
//
// Also toggles the edge-shadow modifiers on the wrapper while the list
// overflows horizontally. The list's own filter logic (what a selection does)
// lives in the list-specific controller declared next to this one.
export default class extends Controller {
  connect() {
    this.wrapper = this.element.closest(".lc-filter-tabs")
    this.boundKeydown = this.keydown.bind(this)
    this.boundUpdateOverflow = this.updateOverflow.bind(this)
    this.element.addEventListener("keydown", this.boundKeydown)
    this.element.addEventListener("scroll", this.boundUpdateOverflow, { passive: true })
    window.addEventListener("resize", this.boundUpdateOverflow)
    requestAnimationFrame(() => this.updateOverflow())
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.boundKeydown)
    this.element.removeEventListener("scroll", this.boundUpdateOverflow)
    window.removeEventListener("resize", this.boundUpdateOverflow)
  }

  keydown(event) {
    // Synthetic events from extensions carry no string `key`. LUC-713.
    if (typeof event.key !== "string") return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    const tabs = this.focusableTabs
    const index = tabs.indexOf(event.target)
    if (index === -1) return

    if (NEXT_KEYS.has(event.key)) return this.#moveFocus(event, tabs, (index + 1) % tabs.length)
    if (PREVIOUS_KEYS.has(event.key)) return this.#moveFocus(event, tabs, (index - 1 + tabs.length) % tabs.length)
    if (event.key === "Home") return this.#moveFocus(event, tabs, 0)
    if (event.key === "End") return this.#moveFocus(event, tabs, tabs.length - 1)

    // Space already checks a radio natively (and fires click). Enter does not.
    if (event.key === "Enter" && event.target.type === "radio" && !event.target.checked) {
      event.preventDefault()
      event.target.click()
    }
  }

  updateOverflow() {
    const wrapper = this.wrapper
    const scroller = this.element
    if (!wrapper) return
    const scrollLeft = scroller.scrollLeft || 0
    const clientWidth = scroller.clientWidth || 0
    const scrollWidth = scroller.scrollWidth || 0

    wrapper.classList.toggle("lc-filter-tabs--overflow-left", scrollLeft > 0)
    wrapper.classList.toggle("lc-filter-tabs--overflow-right", Math.ceil(scrollLeft + clientWidth) < scrollWidth)
  }

  get focusableTabs() {
    return Array.from(this.element.querySelectorAll(".lc-filter-tabs__input, a.lc-filter-tabs__tab")).filter(
      (tab) => !tab.disabled && tab.getAttribute("aria-disabled") !== "true"
    )
  }

  #moveFocus(event, tabs, nextIndex) {
    event.preventDefault()
    const next = tabs[nextIndex]
    next.focus()
    next.closest(".lc-filter-tabs__tab")?.scrollIntoView?.({ block: "nearest", inline: "nearest" })
  }
}
